import React, { memo, useCallback, useMemo } from "react";
import { CircularProgress, Grid, MenuItem } from "@mui/material";
import IpdTextField from "../../../../utils/IpdTextField";
import IpdDateField from "../../../../utils/IpdDateField";
import FormField from "../FormField";
import ConfigurableField from "../ConfigurableField";
import { ADMISSION_TYPES, FINANCIAL_CLASSES } from "../../constants/registrationConstants";
import { usePayersByFinanceClass } from "../../hooks/usePayersByFinanceClass";

import { CONFIGURED_SECTION_IDS } from "../../utils/sectionConfiguration";

const S = CONFIGURED_SECTION_IDS;

// Financial classes that are settled through a TPA.
const TPA_CLASSES = ["INSURANCE", "GOVT_SCHEME"];

function Insurance({ patient, setPatient, updateEncounter, errors = {} }) {
  const financialClass = patient.encounter.financialClass || "";
  const showEmployeeId = ["CORP", "STAFF"].includes(financialClass);
  const showPayer = Boolean(financialClass);
  const { payers, loading: loadingPayers } = usePayersByFinanceClass(financialClass);
  const showTpa = TPA_CLASSES.includes(financialClass);
  const { payers: tpaPayers, loading: loadingTpaPayers } = usePayersByFinanceClass(showTpa ? "TPA" : "");

  const tpaOptions = useMemo(() => {
    const { tpaCode, tpaName } = patient.encounter;
    const options = [...tpaPayers];
    if (tpaCode && !options.some((option) => option.value === tpaCode)) {
      options.unshift({ label: tpaName || tpaCode, value: tpaCode });
    }
    return options;
  }, [tpaPayers, patient.encounter]);

  const handleTpaChange = useCallback((code) => {
    const match = tpaOptions.find((option) => option.value === code);
    setPatient((current) => ({
      ...current,
      encounter: { ...current.encounter, tpaCode: code, tpaName: match?.label || "" },
    }));
  }, [setPatient, tpaOptions]);

  const payerOptions = useMemo(() => {
    const currentPayer = patient.encounter.payer || "";
    const options = [...payers];
    if (currentPayer && !options.some((option) => option.value === currentPayer)) {
      options.unshift({ label: currentPayer, value: currentPayer });
    }
    return options;
  }, [payers, patient.encounter.payer]);

  const handleFinancialClassChange = useCallback((nextFinancialClass) => {
    setPatient((current) => ({
      ...current,
      encounter: {
        ...current.encounter,
        financialClass: nextFinancialClass,
        payer: "",
        employeeId: ["CORP", "STAFF"].includes(nextFinancialClass)
          ? current.encounter.employeeId || ""
          : "",
        tpaCode: TPA_CLASSES.includes(nextFinancialClass) ? current.encounter.tpaCode || "" : "",
        tpaName: TPA_CLASSES.includes(nextFinancialClass) ? current.encounter.tpaName || "" : "",
      },
    }));
  }, [setPatient]);

  return (
    <Grid container spacing={2}>
      <ConfigurableField sectionId={S.finance} fieldName="admissionType" xs={12} md={4}>
        {({ required }) => (
          <FormField label="Admission Type" required={required} error={errors.admissionType}>
            <IpdTextField select fullWidth size="small" value={patient.encounter.admissionType} onChange={(e) => updateEncounter("admissionType", e.target.value)} error={Boolean(errors.admissionType)}>
              {ADMISSION_TYPES.map((x) => <MenuItem key={x} value={x}>{x}</MenuItem>)}
            </IpdTextField>
          </FormField>
        )}
      </ConfigurableField>
      <ConfigurableField sectionId={S.finance} fieldName="financialClass" xs={12} md={4}>
        {({ required }) => (
          <FormField label="Financial Class" required={required} error={errors.financialClass}>
            <IpdTextField select fullWidth size="small" value={financialClass} onChange={(e) => handleFinancialClassChange(e.target.value)} error={Boolean(errors.financialClass)}>
              {FINANCIAL_CLASSES.map(([v, l]) => <MenuItem key={v} value={v}>{l}</MenuItem>)}
            </IpdTextField>
          </FormField>
        )}
      </ConfigurableField>
      {showPayer && (
        <ConfigurableField sectionId={S.finance} fieldName="payer" xs={12} md={4}>
          {({ required }) => (
            <FormField label="Payer" required={required} error={errors.payer}>
              <IpdTextField
                select
                fullWidth
                size="small"
                value={patient.encounter.payer || ""}
                onChange={(e) => updateEncounter("payer", e.target.value)}
                disabled={loadingPayers}
                displayEmpty
                error={Boolean(errors.payer)}
              >
                <MenuItem value="">
                  {loadingPayers ? "Loading payers…" : "Select payer"}
                </MenuItem>
                {loadingPayers ? (
                  <MenuItem disabled value="__loading">
                    <CircularProgress size={16} sx={{ mr: 1 }} /> Loading…
                  </MenuItem>
                ) : (
                  payerOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))
                )}
              </IpdTextField>
            </FormField>
          )}
        </ConfigurableField>
      )}
      {showTpa && (
        <ConfigurableField sectionId={S.finance} fieldName="tpaPayer" xs={12} md={4}>
          {({ required }) => (
            <FormField label="TPA Payers" required={required} error={errors.tpaPayer}>
              <IpdTextField
                select
                fullWidth
                size="small"
                value={patient.encounter.tpaCode || ""}
                onChange={(e) => handleTpaChange(e.target.value)}
                disabled={loadingTpaPayers}
                displayEmpty
                error={Boolean(errors.tpaPayer)}
              >
                <MenuItem value="">
                  {loadingTpaPayers ? "Loading TPAs…" : "Select TPA"}
                </MenuItem>
                {loadingTpaPayers ? (
                  <MenuItem disabled value="__loading">
                    <CircularProgress size={16} sx={{ mr: 1 }} /> Loading…
                  </MenuItem>
                ) : (
                  tpaOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))
                )}
              </IpdTextField>
            </FormField>
          )}
        </ConfigurableField>
      )}
      {showEmployeeId && (
        <ConfigurableField sectionId={S.finance} fieldName="employeeId" xs={12} md={6}>
          {({ required }) => (
            <FormField label="Employee ID" required={required} error={errors.employeeId}>
              <IpdTextField fullWidth size="small" value={patient.encounter.employeeId || ""} onChange={(e) => updateEncounter("employeeId", e.target.value)} error={Boolean(errors.employeeId)} />
            </FormField>
          )}
        </ConfigurableField>
      )}
      <ConfigurableField sectionId={S.finance} fieldName="insurancePolicyNo" xs={12} md={6}>
        {({ required }) => (
          <FormField label="Policy Number" required={required} error={errors.insurancePolicyNo}>
            <IpdTextField fullWidth size="small" value={patient.encounter.insurancePolicyNo} onChange={(e) => updateEncounter("insurancePolicyNo", e.target.value)} error={Boolean(errors.insurancePolicyNo)} />
          </FormField>
        )}
      </ConfigurableField>
      <ConfigurableField sectionId={S.finance} fieldName="policyExpiry" xs={12} md={6}>
        {({ required }) => (
          <FormField label="Policy Expiry Date" required={required} error={errors.policyExpiry}>
            <IpdDateField
              value={patient.encounter.policyExpiry}
              onChange={(value) => updateEncounter("policyExpiry", value)}
              error={Boolean(errors.policyExpiry)}
            />
          </FormField>
        )}
      </ConfigurableField>
    </Grid>
  );
}

export default memo(Insurance);
